import React, { useState, useRef, useEffect } from "react";

/**
 * Các chế độ suy luận (thinking) gửi lên Qwen qua /anthropic/v1/messages.
 * - thinking: bật suy luận đầy đủ (thinking_enabled=true, auto_thinking=false)
 * - auto: để model tự quyết (thinking_enabled=true, auto_thinking=true)
 * - fast: tắt suy luận, trả lời nhanh (thinking_enabled=false)
 */
export const THINKING_OPTIONS = [
  {
    id: "thinking",
    icon: "🧠",
    label: "Suy luận",
    shortLabel: "Suy luận",
    description: "Bật suy luận đầy đủ trước khi trả lời",
  },
  {
    id: "auto",
    icon: "✨",
    label: "Tự động",
    shortLabel: "Tự động",
    description: "Để model tự quyết khi nào cần suy luận",
  },
  {
    id: "fast",
    icon: "⚡",
    label: "Nhanh",
    shortLabel: "Nhanh",
    description: "Tắt suy luận, trả lời nhanh nhất",
  },
  {
    id: "research",
    icon: "🔬",
    label: "Nghiên cứu chuyên sâu",
    shortLabel: "Chuyên sâu",
    description: "Suy luận sâu + tìm kiếm web để nghiên cứu kỹ",
  },
];

/**
 * ThinkingModeSelector — dropdown chọn chế độ suy luận, đặt cạnh PimathToolbar.
 */
function ThinkingModeSelector({ thinkingMode = "auto", onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const current =
    THINKING_OPTIONS.find((o) => o.id === thinkingMode) || THINKING_OPTIONS[1];

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors ${thinkingMode !== "auto"
          ? "border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100"
          : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
          }`}
        title="Chế độ suy luận"
      >
        <span>{current.icon}</span>
        <span className="hidden sm:inline">{current.shortLabel}</span>
        <svg
          className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {open && (
        <div
          className="absolute bottom-full left-0 mb-1 w-60 bg-white border border-gray-200 rounded-xl shadow-lg z-50 py-1"
          style={{ animation: "pimSlideUp 0.12s ease-out" }}
        >
          <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
            Chế độ suy luận
          </div>
          {THINKING_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => {
                onChange?.(opt.id);
                setOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center gap-2 transition-colors ${thinkingMode === opt.id
                ? "bg-purple-50 text-purple-700 font-medium"
                : "text-gray-700"
                }`}
            >
              <span className="text-sm flex-shrink-0">{opt.icon}</span>
              <div className="min-w-0 flex-1">
                <div className="font-medium">{opt.label}</div>
                <div className="text-[10px] text-gray-500 truncate">
                  {opt.description}
                </div>
              </div>
              {thinkingMode === opt.id && (
                <svg
                  className="w-3.5 h-3.5 text-purple-600 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default ThinkingModeSelector;
